/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */

// §6-7 — a simple, SITE-SCOPED public cart. Keyed by website slug so a cart on Site A
// can never contain (or even see) Site B's items — there is no shared/global cart key
// anywhere in this module. Deliberately client-side only: the cart is a shopping list
// of {product_id, variant_id, quantity}, never a price or total — every price/total
// shown while browsing is a live read from the catalog API, and the actual order total
// is always computed authoritatively by the backend at checkout (§22, §27). Losing a
// cart on a cleared browser is an acceptable tradeoff for a "simple checkout
// appropriate for the current architecture" (§8) — there is no existing cart backend
// to build on, and inventing a stateful one was out of this task's stated scope.

function cartKey(slug) {
  return `mysitecare-store-cart-${slug}`;
}

export function getCart(slug) {
  try {
    const raw = window.localStorage.getItem(cartKey(slug));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCart(slug, items) {
  try { window.localStorage.setItem(cartKey(slug), JSON.stringify(items)); } catch { /* ignore */ }
  // Same-tab listeners (e.g. the cart-count badge in StoreShell) never see the native
  // "storage" event, which only fires in OTHER tabs — dispatch our own so this tab's
  // UI updates immediately too, matching the pattern already used for cookie consent.
  window.dispatchEvent(new CustomEvent("mysitecare:store-cart-changed", { detail: { slug } }));
}

function lineKey(item) {
  return `${item.product_id}::${item.variant_id || ""}`;
}

export function addToCart(slug, { product_id, variant_id, quantity, name, variant_name, unit_price_minor, image }) {
  const items = getCart(slug);
  const key = `${product_id}::${variant_id || ""}`;
  const existing = items.find((i) => lineKey(i) === key);
  if (existing) {
    existing.quantity += quantity;
  } else {
    items.push({ product_id, variant_id: variant_id || null, quantity, name, variant_name, unit_price_minor, image });
  }
  saveCart(slug, items);
  return items;
}

export function updateCartQuantity(slug, product_id, variant_id, quantity) {
  let items = getCart(slug);
  const key = `${product_id}::${variant_id || ""}`;
  if (quantity <= 0) {
    items = items.filter((i) => lineKey(i) !== key);
  } else {
    const existing = items.find((i) => lineKey(i) === key);
    if (existing) existing.quantity = quantity;
  }
  saveCart(slug, items);
  return items;
}

export function removeFromCart(slug, product_id, variant_id) {
  return updateCartQuantity(slug, product_id, variant_id, 0);
}

export function clearCart(slug) {
  saveCart(slug, []);
}

export function cartTotalMinor(items) {
  return items.reduce((sum, i) => sum + i.unit_price_minor * i.quantity, 0);
}

export function cartItemCount(items) {
  return items.reduce((sum, i) => sum + i.quantity, 0);
}
